import {
    Component,
    DOCUMENT,
    effect,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    signal,
    SimpleChanges
} from '@angular/core';
import {FormsModule} from "@angular/forms";

@Component({
    selector: 'app-search-payment-terms',
    imports: [FormsModule],
    templateUrl: './search-payment-terms.component.html',
    standalone: true,
    styleUrl: './search-payment-terms.component.scss'
})
export class SearchPaymentTermsComponent implements OnInit, OnDestroy, OnChanges {
    isListVisible = signal(false);
    searchTerm = signal<string | undefined>(undefined);
    paymentTermValue = signal<string | undefined>(undefined);
    disabled = input<boolean>(false);
    resetTrigger = input<boolean>(false);

    @Input() paymentTermInput: string | undefined;
    @Output() selectPaymentTerm = new EventEmitter<string>();
    
    // Hardcoded payment terms
    paymentTerms = signal([
        { value: 'cod', text: 'Cash On Delivery' },
        { value: 'cr', text: 'Credit' }
    ]);
    
    filteredOptions = signal<{ value: string; text: string }[]>([]);

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private elementRef: ElementRef
    ) {
        effect(() => {
            if (this.resetTrigger()) {
                this.resetSearchInput();
            }
        });
    }

    ngOnInit(): void {
        this.filteredOptions.set(this.paymentTerms());
        this.document.addEventListener('keydown', this.handleEscape, false);

        if (this.paymentTermInput) {
            this.setSelectedPaymentTerm(this.paymentTermInput);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['paymentTermInput']) {
            this.handlePaymentTermInputChange(changes['paymentTermInput'].currentValue);
        }
    }

    ngOnDestroy(): void {
        this.document.removeEventListener('keydown', this.handleEscape, false);
    }

    private handlePaymentTermInputChange(newValue: string | undefined): void {
        if (newValue === '') {
            this.clearSelection();
        } else if (newValue) {
            this.setSelectedPaymentTerm(newValue);
        }
    }

    private setSelectedPaymentTerm(value: string): void {
        const term = this.paymentTerms().find(option => option.value === value);
        if (term) {
            this.searchTerm.set(term.text);
            this.paymentTermValue.set(term.value);
            this.selectPaymentTerm.emit(term.value);
        } else {
            this.clearSelection();
        }
    }

    onOptionSelected(value: string): void {
        this.setSelectedPaymentTerm(value);
        this.isListVisible.set(false);
    }

    onInputChange(value: string): void {
        this.searchTerm.set(value);
        if (!value) {
            this.clearSelection();
            this.filteredOptions.set(this.paymentTerms());
        } else {
            const filtered = this.paymentTerms().filter(option =>
                option.text.toLowerCase().includes(value.toLowerCase())
            );
            this.filteredOptions.set(filtered);
        }
    }

    clearSelection(): void {
        this.searchTerm.set('');
        this.paymentTermValue.set(undefined);
        this.isListVisible.set(false);
        this.selectPaymentTerm.emit('');
    }

    @HostListener('document:click', ['$event'])
    clickOutsideDropdown(event: Event): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isListVisible.set(false);
        }
    }

    private handleEscape = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') {
            this.isListVisible.set(false);
        }
    };

    resetSearchInput = (): void => {
        this.clearSelection();
    };
}